import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { handleNonGlobalApiError } from "@/features/error/handleError";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { FieldMetadataDisplay } from "@/pages/applications/pages/customFields/components/FieldMetadataDisplay";

type CustomField = InterludeTypes["CustomField"];

interface RestoreFieldModalProps {
  open: boolean;
  field: CustomField | null;
  onClose: () => void;
  onSuccess: () => void;
}

const styles = {
  infoList: {
    margin: 0,
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const RestoreFieldModal: React.FC<RestoreFieldModalProps> =
  function RestoreFieldModal({ open, field, onClose, onSuccess }) {
    const { t } = useTranslation();
    const [apiError, setApiError] = useState<string | null>(null);

    const restoreMutation = interlude.customField.useRestoreCustomField(
      {
        params: {
          fieldId: field?.id ?? 0,
        },
      },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: async (error) => {
          await handleNonGlobalApiError(error, {
            CUSTOM_FIELD_ACTIVE_LIMIT_EXCEEDED() {
              setApiError(t("customField.page.limitReached"));
            },
            CUSTOM_FIELD_SLOT_LIMIT_EXCEEDED() {
              setApiError(t("customField.error.slotLimitExceeded"));
            },
          });
        },
      },
    );

    const handleClose = useHandler(() => {
      setApiError(null);
      onClose();
    });

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      },
    );

    const onSubmit = useHandler(() => {
      if (!field) return;
      setApiError(null);
      restoreMutation.mutate(undefined);
    });

    const footer: React.ReactNode = useMemo(
      () => (
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={handleClose}>
            {t("customField.restoreModal.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={!field}
            loading={restoreMutation.isPending}
          >
            {t("customField.restoreModal.submit")}
          </Button>
        </Stack>
      ),
      [handleClose, onSubmit, field, restoreMutation.isPending, t],
    );

    if (!field) {
      return null;
    }

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={t("customField.restoreModal.title")}
        width={520}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("customField.restoreModal.description")}
          </Typography>

          {apiError ? (
            <InfoBox
              variant="error"
              title={t("customField.error.configurationFailed")}
              description={apiError}
            />
          ) : null}

          {/* Info InfoBox */}
          <InfoBox
            variant="primary"
            title={t("customField.restoreModal.info.title")}
            description={
              <Box component="ul" sx={styles.infoList}>
                <Box component="li">
                  <Typography
                    variant="body"
                    color={theme.colors.staticFgPrimary}
                  >
                    {t("customField.restoreModal.info.dataIntact")}
                  </Typography>
                </Box>
                <Box component="li">
                  <Typography
                    variant="body"
                    color={theme.colors.staticFgPrimary}
                  >
                    {t("customField.restoreModal.info.immediatelyAvailable")}
                  </Typography>
                </Box>
              </Box>
            }
          />

          {/* Field Metadata Display */}
          <FieldMetadataDisplay
            field={field}
            translationKeyPrefix="customField.restoreModal"
          />
        </Stack>
      </Modal>
    );
  };
